import {ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {Globals} from '../../../../globals';
import {StaticDataService} from '../../../../services/static-data.service';
import {NotesService} from '../../../../services/notes.service';
import {FileManagerService} from '../../../file-manager/file-manager.service';
import {FileHelperService} from '../../../../services/file-helper.service';
import {FinalReportService} from '../../../../services/final-report.service';
import {PatientCaseEndoFinalReportDataModel} from '../../../../models/finalReport/patient-case-endo-final-report-data.model';
import {DataPatientCaseEndoRecallModel} from '../../../recall/models/data-patient-case-endo-recall.model';
import {RecallService} from '../../../../services/recall.service';
import {EnumValidValueItemModel} from '../../../../models/enum/enum-valid-value-item.model';
import {ReportConfigurationModel} from '../../../../models/configuration/report-configuration.model';
import {TreatmentService} from '../../../../services/treatment.service';
import {TreatmentEndoDataModel} from '../../../treatment/models/treatment-endo-data.model';
import {EndoDiagnosisDataModel} from '../diagnosis/models/endo-diagnosis-data.model';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {NoteDataModel} from '../../../../models/notes/note-data.model';
import {TdoNoteAssociatedObject, TdoNoteSaveModel} from '../../../../main-interface/controls/tdo-notes/models/tdo-note-save-model';
import {DocumentTypeEnum} from '../../../../models/enum/document/document-type.enum';
import {DocumentClassEnum} from '../../../../models/enum/document/document-class.enum';
import {PatientCaseEndoRecallAutofillModel} from '../../../recall/models/patient-case-endo-recall-autofill.model';
import {MessagingConstants} from '../../../../models/MessagingConstants';
import {MessagingService} from '../../../../services/messaging.service';
import {TdoNoteQueryModel} from '../../../../main-interface/controls/tdo-notes/models/tdo-note-query.model';
import {DatePipe} from '@angular/common';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {TdoOrganizerComponent} from '../../../../main-interface/controls/tdo-organizer/tdo-organizer.component';
import {TdoDocumentViewerComponent} from '../../../../tdo-controls/common/tdo-document-viewer/tdo-document-viewer.component';
import {DocumentGroupCreationModel} from '../../../../models/document/document-group-creation.model';
import {MatSnackBar} from '@angular/material/snack-bar';
import {EndoRecallFormGetFinalReportDetails} from '../../../recall/models/endo-recall-form-get-final-report-details';
import {firstValueFrom} from 'rxjs';
import {finalize} from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-recall-v2',
  templateUrl: './recall-v2.component.html',
  styleUrls: ['./recall-v2.component.scss']
})
export class RecallV2Component implements OnInit {
  @Input() currentCaseId: string;
  @Input() currentCaseWorkItemFormsId: string;

  @ViewChild('rightColumn') rightColumn: ElementRef;
  @ViewChild('documentViewer') docViewer: TdoDocumentViewerComponent;

  // Using PatientCaseId we can get the recall data
  // From the recall data we can get the final report
  // From the final report we have access to CaseWorkItemId

  isLoading: boolean = true;
  isCreatingRecall: boolean = false;
  isSendingRecall: boolean = false;
  private recallRequestQueue: Promise<void> = Promise.resolve();
  recalls: DataPatientCaseEndoRecallModel[] = [];
  selectedRecall: DataPatientCaseEndoRecallModel;
  finalReport: PatientCaseEndoFinalReportDataModel = null;
  finalReportData: EndoRecallFormGetFinalReportDetails = null;
  treatment: TreatmentEndoDataModel = null;
  diagnosis: EndoDiagnosisDataModel = null;
  autoconfigs: PatientCaseEndoRecallAutofillModel[] = [];

  selectedTabIndex = 0;

  nextRecallEnumId: string = '';
  onOrAroundDate: Date;
  isSuperRecall: boolean;

  probingValues: EnumValidValueItemModel[] = [];
  sinusValues: EnumValidValueItemModel[] = [];
  findingValues: EnumValidValueItemModel[] = [];
  mobilityValues: EnumValidValueItemModel[] = [];
  percussValues: EnumValidValueItemModel[] = [];
  palpValues: EnumValidValueItemModel[] = [];
  pdlidValues: EnumValidValueItemModel[] = [];
  lucencyValues: EnumValidValueItemModel[] = [];
  asymValues: EnumValidValueItemModel[] = [];
  coronalValues: EnumValidValueItemModel[] = [];
  restoredValues: EnumValidValueItemModel[] = [];
  timeValues: EnumValidValueItemModel[] = [];
  docTags: EnumValidValueItemModel[] = [];
  nextRecallOpts: EnumValidValueItemModel[] = [];
  statusEnums: EnumValidValueItemModel[] = [];
  recallEnums: EnumValidValueItemModel[] = [];
  prognosisEnums: EnumValidValueItemModel[] = [];
  medicalSpecialtyEnums: EnumValidValueItemModel[] = [];
  caseTypeEnums: EnumValidValueItemModel[] = [];
  restorationMethodEnums: EnumValidValueItemModel[] = [];
  treatmentSessionsEnums: EnumValidValueItemModel[] = [];
  caseRatingEnums: EnumValidValueItemModel[] = [];

  reports: ReportConfigurationModel[] = [];

  private destroyed = false;

  constructor(public globals: Globals,
              private snackBar: MatSnackBar,
              public recallService: RecallService,
              public messagingService: MessagingService,
              public staticDataService: StaticDataService,
              public noteService: NotesService,
              public fileService: FileManagerService,
              public fileHelper: FileHelperService,
              public reportService: FinalReportService,
              public fileManagerService: FileManagerService,
              public treatmentService: TreatmentService,
              public dialog: MatDialog,
              public dialogRef: MatDialogRef<RecallV2Component>,
              private readonly cdr: ChangeDetectorRef,
              private readonly destroyRef: DestroyRef) {
    destroyRef.onDestroy(() => { this.destroyed = true; });
    if (!this.staticDataService.isReady) {
      this.staticDataService.validValuesReadyEvent.subscribe({
        complete: () => {
          this.setupEnums();
          if (!this.destroyed) { this.cdr.detectChanges(); }
        }
      });
    } else {
      this.setupEnums();
    }
  }

  ngOnInit(): void {
    this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const res = await firstValueFrom(this.recallService.getRecalls(this.currentCaseWorkItemFormsId));
      this.recalls = res.sort((a, b) => {
        const ad = new Date(a.xauditCreatedOn).getTime();
        const bd = new Date(b.xauditCreatedOn).getTime();
        return bd - ad;
      });
      if (this.recalls.length === 0) {
        const rec = new DataPatientCaseEndoRecallModel();
        rec.xauditCreatedOn = new Date();
        rec.PatientCaseId = this.currentCaseId;
        this.recalls.push(rec);
        this.selectedRecall = rec;
      } else {
        this.selectedRecall = this.recalls[0];
      }
      const noteQueryModel = new TdoNoteQueryModel();
      noteQueryModel.parentObjectName = 'dataPatientCaseEndoRecall';
      noteQueryModel.objectId = this.selectedRecall.CaseRecallID;
      noteQueryModel.patientId = this.globals.currentPatientId;
      this.noteService.changeNoteQuery(noteQueryModel);

      const [fpResult, treatmentResult, afResult] = await Promise.allSettled([
        firstValueFrom(this.recallService.getFinalReportData(this.selectedRecall.CaseFinalReportID)),
        firstValueFrom(this.treatmentService.getTreatment(this.globals.currentPatientId, this.currentCaseId)),
        firstValueFrom(this.recallService.getAutoFills()),
      ]);

      this.finalReportData = fpResult.status === 'fulfilled' ? fpResult.value : null;
      if (fpResult.status === 'rejected') { console.error('RecallV2: getFinalReportData failed', fpResult.reason); }

      this.treatment = treatmentResult.status === 'fulfilled' ? treatmentResult.value : null;
      if (treatmentResult.status === 'rejected') { console.error('RecallV2: getTreatment failed', treatmentResult.reason); }

      this.autoconfigs = afResult.status === 'fulfilled' ? afResult.value : [];
      if (afResult.status === 'rejected') { console.error('RecallV2: getAutoFills failed', afResult.reason); }
      
    } catch (err) {
      console.error('RecallV2Component init error:', err);
    } finally {
      this.isLoading = false;
      if (!this.destroyed) { this.cdr.detectChanges(); }
    }
  }

  setupEnums() {
    this.probingValues = this.staticDataService.getValidValueItemsByValidValueName('enumProbingDepth');
    this.sinusValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallSinusTrack');
    this.findingValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallCurrentFinding');
    this.mobilityValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallMobility');
    this.percussValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallPercuss');
    this.palpValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallPalpSore');
    this.pdlidValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallPDL');
    this.lucencyValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallLucencyI');
    this.asymValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallAsymFunc');
    this.coronalValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallCoronalLeak');
    this.restoredValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallToothResored');
    this.timeValues = this.staticDataService.getValidValueItemsByValidValueName('enumEndoRecallInTime');
    const allTags = this.staticDataService.getValidValueItemsByValidValueName('enumDocumentTag');
    this.nextRecallOpts = this.staticDataService.getValidValueItemsByValidValueName('enumFinalReportRecall');
    this.statusEnums = this.staticDataService.getValidValueItemsByValidValueName('enumFinalReportCaseStatus');
    this.prognosisEnums = this.staticDataService.getValidValueItemsByValidValueName('enumFinalReportPrognosis');
    this.recallEnums = this.staticDataService.getValidValueItemsByValidValueName('enumFinalReportRecall');
    this.medicalSpecialtyEnums = this.staticDataService.getValidValueItemsByValidValueName('enumMedicalSpecialty');
    this.caseTypeEnums = this.staticDataService.getValidValueItemsByValidValueName('enumCaseType');
    this.restorationMethodEnums = this.staticDataService.getValidValueItemsByValidValueName('enumFinalReportRestorationMethod');
    this.treatmentSessionsEnums = this.staticDataService.getValidValueItemsByValidValueName('enumTreatmentSessions');
    this.caseRatingEnums = this.staticDataService.getValidValueItemsByValidValueName('enumCaseRating');
  }

  onTabChange(event: MatTabChangeEvent) {
    // this.tabPanelTreatmentPlanSelectedIndex = event.index;
    // this.selectedTreatmentPlan = this.currentTreatmentPlans[this.tabPanelTreatmentPlanSelectedIndex];
    // this.proceduresTable.updateData(this.selectedTreatmentPlan.Procedures);
    this.selectedRecall = this.recalls[this.selectedTabIndex];
    const noteQueryModel = new TdoNoteQueryModel();
    noteQueryModel.parentObjectName = 'dataPatientCaseEndoRecall';
    noteQueryModel.objectId = this.selectedRecall.CaseRecallID;
    noteQueryModel.patientId = this.globals.currentPatientId;
    this.noteService.changeNoteQuery(noteQueryModel);
  }

  getDate(dte: Date) {
    if (dte) {
      return new Date(dte).toDateString();
    }

    return 'No data';
  }

  // getRestorativeRecommendations() {
  //   if (!this.finalReport || (this.finalReport && !this.finalReport.dataPatientCaseEndoFinalReportRestorRecomds)) {
  //     return null;
  //   }
  //   let ret = '';
  //   let i = 0;
  //   for (const recom of this.finalReport?.dataPatientCaseEndoFinalReportRestorRecomds) {
  //     const enu = this.restorationMethodEnums.find(p => p.ValidValueItemId === recom.enumFinalReportRestorationMethodID);
  //     if (enu) {
  //       ret += (i + 1) + '. ' + enu.ItemDisplayValue;
  //     }
  //     if (i < this.finalReport.dataPatientCaseEndoFinalReportRestorRecomds.length - 1) {
  //       ret += '\r\n';
  //     }
  //     i++;
  //   }
  //
  //   return ret;
  // }

  getEnumDisplayValue(enums: EnumValidValueItemModel[], id: string) {
    const enu = enums.find(p => p.ValidValueItemId === id);
    if (id) {
      return enu.ItemDisplayValue;
    }

    return '';
  }

  onCloseTabClick(rec: DataPatientCaseEndoRecallModel) {
    this.recallService.deleteRecall(rec).subscribe((res) => {
      const idx = this.recalls.findIndex(p => p.CaseRecallID === rec.CaseRecallID);
      if (idx > -1) {
        let doSelectAnother = false;
        if (this.selectedRecall === this.recalls[idx]) {
          doSelectAnother = true;
        }
        this.recalls.splice(idx, 1);
        if (this.recalls.length > 0 && doSelectAnother) {
          this.selectedRecall = this.recalls[0];
        }
      }
    });
  }

  getTabName(rec: DataPatientCaseEndoRecallModel) {
    let ret = '';
    if (rec.enumEndoRecallInTimeID) {
      ret += this.getEnumDisplayValue(this.timeValues, rec.enumEndoRecallInTimeID) + ' ';
    }
    const datePipe = new DatePipe('en-US');

    const d = new Date(rec.xauditCreatedOn);
    ret += datePipe.transform(d, 'shortDate');
    // rec.enumEndoRecallInTimeID
    return ret;
  }

  save() {
    const recall = this.selectedRecall;
    recall.ActualRecallDate = new Date();
    this.enqueueRecallRequest(async () => {
      const res = await firstValueFrom(this.recallService.saveRecalls([recall]));
      if (res && res.length > 0) { this.applyServerFields(recall, res[0]); }
    }).catch((err) => console.error('RecallV2: save failed', err))
      .finally(() => { if (!this.destroyed) { this.cdr.detectChanges(); } });
  }

  setSendRecallReport(checked: boolean) {
    this.selectedRecall.isSendRecallReport = checked;
    this.save();
  }

  sendRecall() {
    const recall = this.selectedRecall;
    if (!recall?.CaseRecallID || this.isSendingRecall) { return; }
    this.isSendingRecall = true;
    this.enqueueRecallRequest(async () => {
      this.applyServerFields(recall, await firstValueFrom(this.recallService.sendRecall(recall)));
    }).then(
      () => this.createMessage('Recall report marked as sent', 'Success'),
      (err) => {
        console.error('RecallV2: sendRecall failed', err);
        this.createMessage('The recall report could not be marked as sent', 'Error');
      }
    ).finally(() => {
      this.isSendingRecall = false;
      if (!this.destroyed) { this.cdr.detectChanges(); }
    });
  }

  /**
   * Autosaves and Send Now run one at a time, so an earlier response can never land after a later one
   * (a stale save echo would otherwise wipe the sent stamp from the screen).
   */
  private enqueueRecallRequest(work: () => Promise<void>): Promise<void> {
    const run = this.recallRequestQueue.then(work);
    this.recallRequestQueue = run.catch(() => undefined);
    return run;
  }

  /**
   * Copies only the server-owned fields into the live recall instead of replacing the object: local form
   * edits stay authoritative, and the recall keeps its identity in recalls/selectedRecall.
   */
  private applyServerFields(target: DataPatientCaseEndoRecallModel, res: DataPatientCaseEndoRecallModel) {
    if (!res) { return; }
    target.CaseRecallID = res.CaseRecallID;
    target.xauditCreatedBy = res.xauditCreatedBy;
    target.xauditCreatedOn = res.xauditCreatedOn;
    target.xauditModifiedBy = res.xauditModifiedBy;
    target.xauditModifiedOn = res.xauditModifiedOn;
    target.xauditTableRowID = res.xauditTableRowID;
    target.isRecallReportSent = res.isRecallReportSent;
    target.RecallReportSentOn = res.RecallReportSentOn;
    target.RecallReportSentBy = res.RecallReportSentBy;
  }

  /** RecallReportSentOn is stored in UTC and serialized without a zone; mark it UTC so it displays in local time. */
  get recallReportSentOn(): Date | null {
    const sentOn = this.selectedRecall?.RecallReportSentOn as Date | string;
    if (!sentOn) { return null; }
    if (sentOn instanceof Date) { return sentOn; }
    return new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(sentOn) ? sentOn : sentOn + 'Z');
  }

  createNote() {
    const noteModel = new TdoNoteSaveModel();
    noteModel.associatedObjects = [];
    const as1 = new TdoNoteAssociatedObject();
    as1.parentObjectName = 'dataPatientCaseEndoRecall';
    as1.objectId = this.selectedRecall.CaseRecallID;
    noteModel.associatedObjects.push(as1);

    let noteString = '';
    noteString += 'Date: ' + this.getDate(this.selectedRecall.xauditCreatedOn) + '\r\n';
    noteString += 'Has the tooth been restored? ' + this.getEnumDisplayValue(this.restoredValues, this.selectedRecall.enumEndoRecallToothResoredID) + '\r\n';
    noteString += 'Coronal Leakage? ' + this.getEnumDisplayValue(this.coronalValues, this.selectedRecall.enumEndoRecallCoronalLeakID) + '\r\n';
    noteString += 'Asymptomatic and functional? ' + this.getEnumDisplayValue(this.asymValues, this.selectedRecall.enumEndoRecallAsymFuncID) + '\r\n';
    noteString += 'Has lucency decreased in size? ' + this.getEnumDisplayValue(this.lucencyValues, this.selectedRecall.enumEndoRecallLucencyID) + '\r\n';
    noteString += 'Is PDL normal? ' + this.getEnumDisplayValue(this.pdlidValues, this.selectedRecall.enumEndoRecallPDLID) + '\r\n';
    noteString += 'Palpation soreness: ' + this.getEnumDisplayValue(this.palpValues, this.selectedRecall.enumEndoRecallPalpSoreID) + '\r\n';
    noteString += 'Percussion tests: ' + this.getEnumDisplayValue(this.percussValues, this.selectedRecall.enumEndoRecallPercussID) + '\r\n';
    noteString += 'Mobility: ' + this.getEnumDisplayValue(this.mobilityValues, this.selectedRecall.enumEndoRecallMobilityID) + '\r\n';
    noteString += 'Sinus tract present: ' + this.getEnumDisplayValue(this.sinusValues, this.selectedRecall.enumEndoRecallSinusTrackID) + '\r\n';

    noteString += '\r\nProbing Depths: \r\n';
    noteString += 'M: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingM_enumProbingDepthID) + '\r\n';
    noteString += 'ML: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingML_enumProbingDepthID) + '\r\n';
    noteString += 'MB: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingMB_enumProbingDepthID) + '\r\n';
    noteString += 'L: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingL_enumProbingDepthID) + '\r\n';
    noteString += 'B: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingB_enumProbingDepthID) + '\r\n';
    noteString += 'DL: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingDL_enumProbingDepthID) + '\r\n';
    noteString += 'DB: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingDB_enumProbingDepthID) + '\r\n';
    noteString += 'D: ' + this.getEnumDisplayValue(this.probingValues, this.selectedRecall.probingD_enumProbingDepthID) + '\r\n\r\n';

    noteString += 'Current finding: ' + this.getEnumDisplayValue(this.findingValues, this.selectedRecall.enumEndoRecallCurrentFindingID);

    noteModel.text = noteString;

    noteModel.patientId = this.globals.currentPatientId;
    this.noteService.saveNote(noteModel).subscribe((res) => {
      this.messagingService.sendComponentMessage(MessagingConstants.NOTES_REFRESH, '');
    });
  }

  getWidth() {
    if (this.rightColumn) {
      const w = this.rightColumn.nativeElement.getBoundingClientRect().width;
      return w;
    }
    return 0;
  }

  addRecall(enumEndoRecallInTimeID: string = null) {
    const rec = new DataPatientCaseEndoRecallModel();
    rec.PatientCaseId = this.currentCaseId;
    // console.log(' this.selectedRecall', this.selectedRecall)
    rec.CaseFinalReportID = this.selectedRecall.CaseFinalReportID;
    if (enumEndoRecallInTimeID) {
      rec.enumEndoRecallInTimeID = enumEndoRecallInTimeID;
      this.selectedRecall.enumEndoRecallInTimeID = enumEndoRecallInTimeID;
    }
    rec.for_RecallDate = this.onOrAroundDate;
    //rec.CaseWorkItemFormsId = this.selectedRecall.CaseWorkItemFormsId;
    rec.for_enumEndoRecallInTimeID = this.nextRecallEnumId;

    const locid: number = parseInt(localStorage.getItem('LOCID'));
    const patientid: string = localStorage.getItem('currentPatientId');
    const cwiformsid: string = localStorage.getItem('currentPatientCaseWorkItemFormsID');

    const datepipe: DatePipe = new DatePipe('en-US');
    const formattedDate = datepipe.transform(this.onOrAroundDate, 'dd-MMM-yyyy');


    this.isCreatingRecall = true;
    if (!this.destroyed) { this.cdr.detectChanges(); }

    this.recallService.createNewRecall(locid, patientid, this.selectedRecall.PatientCaseId, cwiformsid, formattedDate, rec)
      .pipe(finalize(() => {
        this.isCreatingRecall = false;
        if (!this.destroyed) { this.cdr.detectChanges(); }
      }))
      .subscribe({
        next: (res) => {
          this.recalls.push(res);
          this.selectedRecall = res;
          this.recalls = this.recalls.sort((a, b) => {
            const ad = new Date(a.xauditCreatedOn).getTime();
            const bd = new Date(b.xauditCreatedOn).getTime();
            return bd - ad;
          });
          this.createMessage('Recall Created', 'Success');
          setTimeout(() => {
            this.messagingService.sendComponentMessage(MessagingConstants.CASES_REFRESH, {});
          }, 0);
        },
        error: (err) => {
          this.createMessage('Error', `Error creating recall: ${err.error.ExceptionMessage ?? err.error.Message ?? err.error}`);
        }
      });
  }

  setAutofills(num: number) {
    const entry = this.autoconfigs?.[num];
    const config = entry?.cfgPatientCaseEndoRecallAutofillDetails?.[0];
    if (!config) { return; }
    this.selectedRecall.enumEndoRecallToothResoredID = config.enumEndoRecallToothResoredID;
    this.selectedRecall.enumEndoRecallCoronalLeakID = config.enumEndoRecallCoronalLeakID;
    this.selectedRecall.enumEndoRecallAsymFuncID = config.enumEndoRecallAsymFuncID;
    this.selectedRecall.enumEndoRecallLucencyID = config.enumEndoRecallLucencyID;
    this.selectedRecall.enumEndoRecallPDLID = config.enumEndoRecallPDLID;
    this.selectedRecall.enumEndoRecallPalpSoreID = config.enumEndoRecallPalpSoreID;
    this.selectedRecall.enumEndoRecallPercussID = config.enumEndoRecallPercussID;
    this.selectedRecall.enumEndoRecallMobilityID = config.enumEndoRecallMobilityID;
    this.selectedRecall.enumEndoRecallSinusTrackID = config.enumEndoRecallSinusTrackID;

    this.selectedRecall.probingM_enumProbingDepthID = config.probingM_enumProbingDepthID;
    this.selectedRecall.probingML_enumProbingDepthID = config.probingML_enumProbingDepthID;
    this.selectedRecall.probingMB_enumProbingDepthID = config.probingMB_enumProbingDepthID;
    this.selectedRecall.probingL_enumProbingDepthID = config.probingL_enumProbingDepthID;
    this.selectedRecall.probingB_enumProbingDepthID = config.probingB_enumProbingDepthID;
    this.selectedRecall.probingDL_enumProbingDepthID = config.probingDL_enumProbingDepthID;
    this.selectedRecall.probingDB_enumProbingDepthID = config.probingDB_enumProbingDepthID;
    this.selectedRecall.probingD_enumProbingDepthID = config.probingD_enumProbingDepthID;

    this.selectedRecall.enumEndoRecallCurrentFindingID = config.enumEndoRecallCurrentFindingID;
    this.save();
  }

  copyPrevious() {
    const currentIndex = this.recalls.findIndex(p => p.CaseRecallID === this.selectedRecall.CaseRecallID);
    const previous = this.recalls[currentIndex + 1];
    if (!previous) { return; }
    this.recallService.copy(this.selectedRecall.CaseRecallID, previous.CaseRecallID).subscribe((res) => {
      this.recalls[currentIndex] = res;
      this.selectedRecall = res;
      this.selectedRecall.probingM_enumProbingDepthID = null;
      this.selectedRecall.probingML_enumProbingDepthID = null;
      this.selectedRecall.probingMB_enumProbingDepthID = null;
      if (!this.destroyed) { this.cdr.detectChanges(); }
    });
  }

  createNextRecall() {
    this.addRecall(this.nextRecallEnumId);
  }

  setNextRecallTime(event: EnumValidValueItemModel) {
    this.nextRecallEnumId = event.ValidValueItemId;
    const todayDate = new Date();
    switch (event.ValidValueItemId) {
      case '00000245-1000-0000-0000-000000000001':
        // None
        break;
      case '00000245-1000-0000-0000-000000000002':
        // 3 Days
        todayDate.setDate(todayDate.getDate() + 3);
        break;
      case '00000245-1000-0000-0000-000000000003':
        // 1 Week
        todayDate.setDate(todayDate.getDate() + 7);
        break;
      case '00000245-1000-0000-0000-000000000004':
        // 2 Weeks
        todayDate.setDate(todayDate.getDate() + 14);
        break;
      case '00000245-1000-0000-0000-000000000005':
        // 3 weeks
        todayDate.setDate(todayDate.getDate() + 21);
        break;
      case '00000245-1000-0000-0000-000000000006':
        // 6 weeks
        todayDate.setDate(todayDate.getDate() + 42);
        break;
      case '00000245-1000-0000-0000-000000000007':
        // 1 month
        todayDate.setDate(todayDate.getDate() + 30);
        break;
      case '00000245-1000-0000-0000-000000000008':
        // 2 month
        todayDate.setDate(todayDate.getDate() + 60);
        break;
      case '00000245-1000-0000-0000-000000000009':
        // 3 month
        todayDate.setDate(todayDate.getDate() + 90);
        break;
      case '00000245-1000-0000-0000-000000000010':
        // 6 month
        todayDate.setDate(todayDate.getDate() + 180);
        break;
      case '00000245-1000-0000-0000-000000000011':
        // 1 year
        todayDate.setDate(todayDate.getDate() + 365);
        break;
      case '00000245-1000-0000-0000-000000000012':
        // 2 year
        todayDate.setDate(todayDate.getDate() + 730);
        break;
    }
    this.onOrAroundDate = todayDate;
  }

  openOrganizer() {
    const config = new MatDialogConfig();
    config.height = '800px';
    config.width = '1300px';
    config.data = {
      PickList: 'true',
      HidePatientTab: false,
      HideLectureTab: false,
      // DocumentGroupID: this.selectedRecall.CaseRecallID,
      // DocumentTags: ['0000001D-1000-0000-0000-000000000088'],
      ReturnDocumentIDMode: true
    };
    this.dialog.open(TdoOrganizerComponent, config).afterClosed().subscribe((result) => {
      // refresh image viewer
      if (result) {
          this.fileService.addMultiDocumentsTags(result, ['0000001D-1000-0000-0000-000000000088']).subscribe((responseAddTag) => {
            const cmodel = new DocumentGroupCreationModel();
            cmodel.documentIds = result;
            cmodel.ObjectId = this.selectedRecall.CaseRecallID;
            cmodel.PatientId = this.globals.currentPatientId;
            this.fileService.createDocumentGroup(cmodel).subscribe((res3) => {
              this.docViewer.checkForDocumentImages();
              this.createMessage('Image added', 'Success');
            });
          });
      }
    });
  }

  createMessage(message, type) {
    this.snackBar.open(message, type, {
      duration: 4000,
      verticalPosition: 'top'
    });
  }
}
